import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Wallet, Plus, Loader2 } from 'lucide-react';

const fmt = (n) => {
  const val = Number(n || 0);
  return val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Header wallet chip. Shows the live SAFER-i™ Unit balance and a "Top Up" action
 * that routes to /billing where the unit packs live (1 unit = SGD 3.99). After
 * a Stripe top-up redirect it polls the status, refreshes, and broadcasts a
 * `units-topped-up` event so the Billing page can refresh its banners.
 */
export default function CreditBalance({ onBalanceChange }) {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBalance();
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const topupStatus = params.get('topup');
    if (sessionId && topupStatus === 'success') {
      pollTopupStatus(sessionId, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBalance = async () => {
    try {
      const response = await api.get('/credits/balance');
      setBalance(response.data.available_credits);
      if (onBalanceChange) onBalanceChange(response.data.available_credits);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollTopupStatus = async (sessionId, attempt) => {
    if (attempt >= 6) {
      toast.info('Payment status check timed out. Check your email for confirmation.');
      return;
    }
    try {
      const res = await api.get(`/credits/topup/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        toast.success('Units added to your wallet!');
        fetchBalance();
        window.dispatchEvent(new CustomEvent('units-topped-up'));
        window.history.replaceState({}, '', window.location.pathname);
      } else if (res.data.status === 'expired') {
        toast.error('Payment session expired.');
      } else {
        setTimeout(() => pollTopupStatus(sessionId, attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollTopupStatus(sessionId, attempt + 1), 2000);
    }
  };

  const getBalanceColor = () => {
    if (balance === null) return 'text-muted-foreground';
    if (balance <= 0) return 'text-red-600';
    if (balance < 50) return 'text-amber-600';
    return 'text-green-600';
  };

  const goToTopUp = () => {
    navigate('/billing');
    // Give the route a beat to render, then scroll to the unit packs.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="unit-packs-card"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="credit-balance-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3" data-testid="credit-balance-container">
      <div className="flex items-center gap-2">
        <Wallet className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Units:</span>
        <span className={`font-semibold tabular-nums ${getBalanceColor()}`} data-testid="credit-balance-value">
          {fmt(balance)}
        </span>
      </div>

      <Button variant="outline" size="sm" className="h-8" onClick={goToTopUp} data-testid="topup-button">
        <Plus className="w-3 h-3 mr-1" />
        Top Up
      </Button>
    </div>
  );
}
